import React, { useState } from "react";
import { ShieldCheck, ShieldAlert, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/** Format an ISO timestamp as `YYYY-MM-DD HH:MM UTC`. */
function fmtTsa(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const yyyy = d.getUTCFullYear();
        const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
        const dd = String(d.getUTCDate()).padStart(2, "0");
        const hh = String(d.getUTCHours()).padStart(2, "0");
        const mn = String(d.getUTCMinutes()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd} ${hh}:${mn} UTC`;
    } catch {
        return iso;
    }
}

function shortHash(h) {
    if (!h) return "—";
    return `…${h.slice(-12)}`;
}

/**
 * Trust pill for fully-signed contracts on the customer dashboard.
 * - status "valid"               → green "Verified by RentFlow"
 * - status "pending" / "failed"  → amber "Verification pending"
 *   (we never show red on the customer surface — admins see the failure detail)
 *
 * Click opens a plain-language explanation modal.
 *
 * Props:
 *   contract: customer contract object from /api/customer/contracts
 */
export function ContractTrustPill({ contract }) {
    const [open, setOpen] = useState(false);
    if (!contract || contract.status !== "fully_signed") return null;

    const rfc = contract.rfc3161_status;            // "valid" | "pending" | "failed" | null
    const chainOk = contract.chain_valid !== false; // null/true → not broken
    const isVerified = rfc === "valid" && chainOk;

    const tooltip = isVerified
        ? `Trusted timestamp by FreeTSA on ${fmtTsa(contract.signed_at_per_tsa)}. Audit chain intact.`
        : "We're finalizing the trusted timestamp for this contract. The signature itself is fully recorded.";

    return (
        <>
            <TooltipProvider delayDuration={150}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            data-testid={`contract-trust-pill-${contract.id}`}
                            data-trust-status={isVerified ? "valid" : "pending"}
                            className={
                                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset transition-colors cursor-pointer " +
                                (isVerified
                                    ? "bg-emerald-50 text-emerald-800 ring-emerald-200 hover:bg-emerald-100"
                                    : "bg-amber-50 text-amber-900 ring-amber-200 hover:bg-amber-100")
                            }>
                            {isVerified ? (
                                <ShieldCheck size={12} className="-ml-0.5" />
                            ) : (
                                <ShieldAlert size={12} className="-ml-0.5" />
                            )}
                            {isVerified ? "Verified by RentFlow" : "Verification pending"}
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-center leading-snug">
                        {tooltip}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    className="sm:max-w-md"
                    data-testid={`contract-trust-modal-${contract.id}`}>
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl flex items-center gap-2">
                            <span
                                className={
                                    "inline-flex h-8 w-8 items-center justify-center rounded-full " +
                                    (isVerified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800")
                                }>
                                {isVerified ? <ShieldCheck size={16} /> : <ShieldAlert size={16} />}
                            </span>
                            {isVerified ? "Verified by RentFlow" : "Verification pending"}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-slate-700 leading-6 pt-2">
                            This contract is cryptographically sealed and timestamped by an independent
                            trusted authority. RentFlow can prove the exact moment it was signed and that
                            it has not been altered since.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="mt-2 grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
                        <span className="col-span-1 text-slate-500">Signed by</span>
                        <span
                            className="col-span-2 text-slate-900 font-medium"
                            data-testid="trust-modal-customer-signer">
                            {contract.customer_signer_name || "—"}
                        </span>

                        <span className="col-span-1 text-slate-500">Countersigned by</span>
                        <span
                            className="col-span-2 text-slate-900 font-medium"
                            data-testid="trust-modal-admin-signer">
                            {contract.admin_signer_name || "—"}
                        </span>

                        <span className="col-span-1 text-slate-500">Signed at</span>
                        <span
                            className="col-span-2 text-slate-900"
                            data-testid="trust-modal-signed-at">
                            {fmtTsa(contract.admin_signed_at)}
                        </span>

                        <span className="col-span-1 text-slate-500">Trusted timestamp</span>
                        <span
                            className="col-span-2 text-slate-900"
                            data-testid="trust-modal-tsa-at">
                            {isVerified ? `${fmtTsa(contract.signed_at_per_tsa)} · FreeTSA` : "Pending"}
                        </span>

                        <span className="col-span-1 text-slate-500">Document hash</span>
                        <span
                            className="col-span-2 font-mono text-xs text-slate-700 break-all"
                            data-testid="trust-modal-hash">
                            {shortHash(contract.signed_pdf_sha256)}
                        </span>
                    </div>

                    {Array.isArray(contract.documents) && contract.documents.length > 1 && (
                        <div className="mt-4 space-y-2"
                            data-testid="trust-modal-docs">
                            <p className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">Documents in this contract</p>
                            {contract.documents.map((d) => {
                                const ok = d.rfc3161_status === "ok";
                                return (
                                    <div key={d.doc_id}
                                        data-testid={`trust-modal-doc-row-${d.doc_type}`}
                                        className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-2.5 py-1.5">
                                        <span className="text-xs font-semibold text-slate-700">
                                            {d.doc_type === "rental_agreement" ? "Rental contract" : "ACH authorization"}
                                        </span>
                                        <span className={
                                            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset " +
                                            (ok
                                                ? "bg-emerald-50 text-emerald-900 ring-emerald-200"
                                                : "bg-amber-50 text-amber-900 ring-amber-200")
                                        }>
                                            {ok
                                                ? (d.signed_at_per_tsa ? fmtTsa(d.signed_at_per_tsa) : "Verified")
                                                : "Pending"}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5 text-xs text-slate-600 leading-5">
                        Want the full audit trail? Ask your account manager — every link open, view, and
                        signature is recorded with cryptographic chaining.
                    </div>

                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
                        data-testid="trust-modal-close"
                        aria-label="Close">
                        <X size={16} />
                    </button>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default ContractTrustPill;
